import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

import { isEmpty } from 'lodash';
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import {
  LetterSpacingClass,
  LetterSpacingStyle,
} from './attributors/letterSpacing';
import { LineHeightClass, LineHeightStyle } from './attributors/lineHeight';
import { COLORS } from './constants/colors';
import { textToolbarFormats } from './constants/textFormats';
import { PlainClipboard } from './DynamicQuillTools';
import {
  applyDefaultStyleValues,
  constructFontSelect,
  constructLetterSpacingSelect,
  constructLineHeightSelect,
  constructSizeSelect,
  constructSpecialCharSelect,
  extractStylesFromHtml,
  isEmptyQuillContent,
  normalizeInitialQuillValue,
} from './RichTextWidget.helpers';
import {
  IRichTextEditorDefaultStyleValues,
  QuillTheme,
  RichTextEditorProps,
  RichTextToolbarOptions,
} from './RichTextWidget.interface';
import { RichTextEditorRoot } from './RichTextWidget.styled';

// Register the custom styles and re-register the styles we want to use inline CSS
const FontFamilyAttributor = Quill.import('attributors/style/font');
FontFamilyAttributor.whitelist = null;
Quill.register(FontFamilyAttributor, true);
const FontSizeAttributor = Quill.import('attributors/style/size');
FontSizeAttributor.whitelist = null;
const Block = Quill.import('blots/block');
Block.tagName = 'Div';
Block.className = 'pre';
Quill.register(Block, true);
Quill.register(FontSizeAttributor, true);
Quill.register(Quill.import('attributors/style/direction'), true);
Quill.register(Quill.import('attributors/style/align'), true);
Quill.register(LetterSpacingClass, true);
Quill.register(LetterSpacingStyle, true);
Quill.register(LineHeightClass, true);
Quill.register(LineHeightStyle, true);

export const RichTextEditor: React.FunctionComponent<RichTextEditorProps> = (
  props,
) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const quillDefaultStylesRef =
    useRef<IRichTextEditorDefaultStyleValues | null>(null);
  const savedSelectionRef = useRef<any>(null);

  const { value = '' } = props;
  const [quillValue, setQuillValue] = useState<string>(props.value || '');
  const [isSuperscriptActive, setIsSuperscriptActive] =
    useState<boolean>(false);

  if (!props?.pasteWithStyle) {
    Quill.register(
      {
        'modules/clipboard': PlainClipboard,
      },
      true,
    );
  }

  const handleChange = (newValue: string, delta: any, source: string) => {
    const valueStyles = extractStylesFromHtml(value);

    if (!isEmpty(valueStyles))
      quillDefaultStylesRef.current = { ...valueStyles };

    if (source === 'api') return;

    setQuillValue(newValue);
    props.useImmediateUpdate
      ? props.onChange(newValue)
      : startTransition(() => {
          props.onChange(newValue);
        });

    if (isEmptyQuillContent(newValue)) {
      const mixedStyles = {
        ...props.defaultStyleValues,
        ...quillDefaultStylesRef.current,
      };
      applyDefaultStyleValues(quillRef.current, mixedStyles);
    }
  };

  // Handle selection change to track superscript state
  const handleSelectionChange = (range: any) => {
    if (!range || !quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const format = quill.getFormat(range.index, range.length);

    setIsSuperscriptActive(!!format.script && format.script === 'super');

    // Save selection for later use
    savedSelectionRef.current = range;
  };

  // Handle editor changes to detect format changes including superscript
  const handleEditorChange = (eventName: string, ...args: any[]) => {
    if (eventName === 'selection-change') {
      handleSelectionChange(args[0]);
    }

    // Also check format on text changes to catch toolbar formatting
    if (eventName === 'text-change' || eventName === 'selection-change') {
      const quill = quillRef.current?.getEditor();
      if (quill && props.enableSuperscriptToolbars) {
        const selection = quill.getSelection();
        if (selection) {
          const format = quill.getFormat(selection.index, selection.length);
          const newSuperscriptState =
            !!format.script && format.script === 'super';

          // Only update if state actually changed
          if (newSuperscriptState !== isSuperscriptActive) {
            setIsSuperscriptActive(newSuperscriptState);
          }
        }
      }
    }
  };

  const handleToolbarClick = (event: Event) => {
    const target = event.target as HTMLElement;

    // Check if superscript button was clicked
    if (
      target.classList.contains('ql-script') ||
      target.closest('.ql-script[value="super"]') ||
      target.getAttribute('value') === 'super'
    ) {
      // Small delay to allow Quill to process the format change
      setTimeout(() => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          if (range) {
            const format = quill.getFormat(range.index, range.length);
            setIsSuperscriptActive(
              !!format.script && format.script === 'super',
            );
          }
        }
      }, 50);
    }
  };

  // Handle text change to prevent manual keyboard input in superscript mode
  const handleTextChange = (delta: any, oldDelta: any, source: string) => {
    if (
      !props.enableSuperscriptToolbars ||
      !isSuperscriptActive ||
      source !== 'user'
    )
      return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Check if the change involves text insertion
    const ops = delta.ops || [];
    let shouldPreventInput = false;

    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];

      // If there's a text insertion
      if (op.insert && typeof op.insert === 'string' && op.insert.length > 0) {
        // Allow special chars from dropdown, prevent everything else
        const defaultChars = ['©', '®', '†', '™', '¶', '§'];
        const allowedChars = props.specialChars?.length
          ? props.specialChars
          : defaultChars;

        // Check each character in the insertion
        for (const char of op.insert) {
          if (!allowedChars.includes(char)) {
            shouldPreventInput = true;
            break;
          }
        }
      }
    }

    if (shouldPreventInput) {
      // Prevent the insertion by undoing it
      setTimeout(() => {
        //@ts-ignore
        quill.history.undo();
      }, 0);
    }
  };

  // Handle keyboard events to provide better UX feedback
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!props.enableSuperscriptToolbars || !isSuperscriptActive) return;

    // Allow navigation keys, backspace, delete, etc.
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Backspace',
      'Delete',
      'Home',
      'End',
      'Tab',
      'Escape',
      'Enter',
    ];

    // Allow Ctrl/Cmd combinations for copy, paste, etc.
    if (event.ctrlKey || event.metaKey) return;

    // If it's a character key and not in allowed keys, prevent it
    if (event.key.length === 1 && !allowedKeys.includes(event.key)) {
      event.preventDefault();
      // Optional: Show a tooltip or message indicating to use Special chars dropdown
    }
  };

  // Custom handler for special char selection that preserves selection
  const handleSpecialCharSelect = (char: string) => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const range = savedSelectionRef.current || quill.getSelection();

    if (range) {
      // Mark this as a special char insertion to bypass the text-change restriction
      quill.insertText(range.index, char, { script: 'super' }, 'user');
      // Set selection to the inserted character so user can modify its size
      quill.setSelection(range.index, char.length);
      savedSelectionRef.current = { index: range.index, length: char.length };
    }
  };

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || !props.enableSuperscriptToolbars) return;
    updateDropdownStates(quill, !isSuperscriptActive);
  }, [isSuperscriptActive, props.enableSuperscriptToolbars]);

  useEffect(() => {
    const normalizedHTML = normalizeInitialQuillValue(value);
    setQuillValue(normalizedHTML);
  }, [value]);

  useEffect(() => {
    const isEmpty =
      !quillValue ||
      isEmptyQuillContent(quillValue) ||
      quillValue.trim() === '<div class="pre"></div>';
    if (isEmpty && props.defaultStyleValues) {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const quillKeyMap: Record<string, string> = {
          fontFamily: 'font',
          fontSize: 'size',
          letterSpacing: 'letter-spacing',
          lineHeight: 'line-height',
          backgroundColor: 'background-color',
        };
        Object.entries(props.defaultStyleValues).forEach(([key, val]) => {
          if (!val) return;
          editor.format(quillKeyMap[key] ?? key, val, 'api');
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillValue]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    // Use Quill's editor-change event which captures all changes
    const editor = quill.getEditor();
    editor.on('editor-change', handleEditorChange);

    // Add text change listener to prevent manual input in superscript mode
    if (props.enableSuperscriptToolbars) {
      editor.on('text-change', handleTextChange);
    }

    // Add keyboard event listener to the editor container
    const editorContainer = editor.root;
    if (props.enableSuperscriptToolbars) {
      editorContainer.addEventListener('keydown', handleKeyDown);
    }

    // Add toolbar click listener to detect superscript button clicks
    const toolbar = editor.getModule('toolbar');
    const toolbarContainer = toolbar?.container;
    if (props.enableSuperscriptToolbars && toolbarContainer) {
      toolbarContainer.addEventListener('click', handleToolbarClick);
    }

    return () => {
      editor.off('editor-change', handleEditorChange);
      if (props.enableSuperscriptToolbars) {
        editor.off('text-change', handleTextChange);
        editorContainer.removeEventListener('keydown', handleKeyDown);
        if (toolbarContainer) {
          toolbarContainer.removeEventListener('click', handleToolbarClick);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.enableSuperscriptToolbars, isSuperscriptActive]);

  const toolbar = useMemo(() => {
    const formats = textToolbarFormats.filter(
      (option) => props.hide.indexOf(option as RichTextToolbarOptions) < 0,
    );
    const superscriptOption = { script: 'super' };

    return [
      [...formats, superscriptOption],
      [
        props.hide.indexOf(RichTextToolbarOptions.COLOR) < 0 && {
          color: props?.color ?? COLORS,
        },
        props.hide.indexOf(RichTextToolbarOptions.BACKGROUND) < 0 && {
          background: COLORS,
        },
      ].filter(Boolean),
      props.hide.indexOf(RichTextToolbarOptions.CLEAN) < 0 ? ['clean'] : [],
      [props.hide.indexOf(RichTextToolbarOptions.ALIGN) < 0 && { align: [] }],
    ];
  }, [props.hide, props?.color]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || props.hideToolbar) return;

    // Handle conditional Special chars and Size toolbars based on enableSuperscriptToolbars prop
    if (props.enableSuperscriptToolbars) {
      if (!props.hide.includes(RichTextToolbarOptions.SPECIAL_CHAR_SELECT)) {
        constructSpecialCharSelect(
          quill,
          props.specialChars,
          handleSpecialCharSelect,
          !isSuperscriptActive,
        );
      }
      if (!props.hide.includes(RichTextToolbarOptions.SIZE)) {
        constructSizeSelect(
          quill,
          props.fontSize,
          savedSelectionRef,
          !isSuperscriptActive,
        );
      }
    } else {
      // Original behavior - always show if not hidden
      if (!props.hide.includes(RichTextToolbarOptions.SPECIAL_CHAR_SELECT)) {
        constructSpecialCharSelect(quill, props.specialChars);
      }
      if (!props.hide.includes(RichTextToolbarOptions.SIZE)) {
        constructSizeSelect(quill, props.fontSize, savedSelectionRef);
      }
    }

    // These remain always available
    if (!props.hide.includes(RichTextToolbarOptions.FONT)) {
      constructFontSelect(quill, props.fontFamily);
    }
    if (!props.hide.includes(RichTextToolbarOptions.LINE_HEIGHT)) {
      constructLineHeightSelect(quill, props.lineHeight);
    }
    if (!props.hide.includes(RichTextToolbarOptions.LETTER_SPACING)) {
      constructLetterSpacingSelect(quill, props.letterSpacing);
    }
  }, [
    props.hideToolbar,
    props.hide,
    props.specialChars,
    props.fontFamily,
    props.fontSize,
    props.letterSpacing,
    props.lineHeight,
    props.enableSuperscriptToolbars,
    isSuperscriptActive, // Add dependency on superscript state
  ]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || !props.defaultStyleValues) return;

    const editor = quill.getEditor();
    const contentLength = editor.getLength() - 1; // Quill always has a trailing newline

    if (contentLength > 0) {
      const firstCharFormat = editor.getFormat(0, 1);
      const quillKeyMap: Record<string, string> = {
        fontFamily: 'font',
        fontSize: 'size',
        letterSpacing: 'letter-spacing',
        lineHeight: 'line-height',
        backgroundColor: 'background-color',
      };

      Object.entries(props.defaultStyleValues).forEach(([key, value]) => {
        if (!value) return;
        const quillKey = quillKeyMap[key] ?? key;
        if (!firstCharFormat[quillKey]) {
          editor.formatText(0, contentLength, quillKey, value, 'user');
        }
      });
    }

    applyDefaultStyleValues(quill, props.defaultStyleValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.defaultStyleValues?.fontFamily,
    props.defaultStyleValues?.fontSize,
    props.defaultStyleValues?.letterSpacing,
    props.defaultStyleValues?.lineHeight,
    props.defaultStyleValues?.color,
    props.defaultStyleValues?.backgroundColor,
  ]);

  const backgroundImage = getThumbnailSrc(props.backgroundImage);

  return (
    <RichTextEditorRoot
      data-testid="quill"
      theme={props.theme}
      $backgroundImage={backgroundImage}
      textColor={props.textColor}
      fontSizeEditor={props.fontSizeEditor}
      lineHeightEditor={props.lineHeightEditor}>
      <ReactQuill
        className="container"
        ref={quillRef}
        value={quillValue}
        theme={props.theme || QuillTheme.TOOLBAR}
        onChange={handleChange}
        placeholder={props.placeholder || ''}
        modules={{
          toolbar: props.hideToolbar ? false : toolbar,
        }}
        readOnly={props.disabled}
      />
    </RichTextEditorRoot>
  );
};

// Helper function to update dropdown states
const updateDropdownStates = (quill: any, isDisabled: boolean) => {
  const toolbar = quill.editor.getModule('toolbar');
  const toolbarContainer = toolbar.container;

  const specialCharsDropdown = toolbarContainer.querySelector(
    `[data-label^="Special chars"]`,
  );
  const sizeDropdown = toolbarContainer.querySelector(`[data-label^="Size"]`);

  if (specialCharsDropdown) {
    if (isDisabled) {
      specialCharsDropdown.style.opacity = '0.5';
      specialCharsDropdown.style.pointerEvents = 'none';
      specialCharsDropdown.setAttribute('disabled', 'true');
    } else {
      specialCharsDropdown.style.opacity = '1';
      specialCharsDropdown.style.pointerEvents = 'auto';
      specialCharsDropdown.removeAttribute('disabled');
    }
  }

  if (sizeDropdown) {
    if (isDisabled) {
      sizeDropdown.style.opacity = '0.5';
      sizeDropdown.style.pointerEvents = 'none';
      sizeDropdown.setAttribute('disabled', 'true');
    } else {
      sizeDropdown.style.opacity = '1';
      sizeDropdown.style.pointerEvents = 'auto';
      sizeDropdown.removeAttribute('disabled');
    }
  }
};
